import { Links, Meta, Outlet, Scripts, ScrollRestoration } from 'react-router';
import type { LinksFunction, MetaFunction } from 'react-router';

import abcDiatypeMediumUrl from '@cartwhl/ui/theme/fonts/ABCDiatype-Medium.woff2?url';
import abcDiatypeRegularUrl from '@cartwhl/ui/theme/fonts/ABCDiatype-Regular.woff2?url';

import './index.css';
import { productionOrigin, publicOrganization } from './public-identity';
import {
  classifyUnderlineChromium,
  classifyUnderlineEngine,
  prepaintUnderlineEngineScript,
} from './underline-engine';

export const meta: MetaFunction = () => [
  { title: 'Cartwheel' },
  {
    name: 'description',
    content:
      'Cartwheel is a generative motion platform for games, film and TV, advertising, and robotics.',
  },
];

// All marketing routes use the shell header and their initial content in Diatype.
// Keep this limited to the regular and medium normal faces.
export const links: LinksFunction = () => [
  {
    rel: 'preload',
    href: abcDiatypeRegularUrl,
    as: 'font',
    type: 'font/woff2',
    crossOrigin: 'anonymous',
  },
  {
    rel: 'preload',
    href: abcDiatypeMediumUrl,
    as: 'font',
    type: 'font/woff2',
    crossOrigin: 'anonymous',
  },
];

const siteStructuredData = JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@id': `${productionOrigin}/#organization`,
      '@type': 'Organization',
      ...publicOrganization,
    },
    {
      '@id': `${productionOrigin}/#website`,
      '@type': 'WebSite',
      name: publicOrganization.name,
      url: productionOrigin,
      publisher: { '@id': `${productionOrigin}/#organization` },
    },
  ],
});

export function Layout({ children }: { children: React.ReactNode }) {
  const underlineEngine =
    typeof navigator === 'undefined'
      ? undefined
      : classifyUnderlineEngine(navigator.userAgent);

  return (
    <html
      lang="en"
      data-underline-engine={underlineEngine}
      data-underline-chromium={
        underlineEngine === 'chromium'
          ? classifyUnderlineChromium(navigator.userAgent)
          : undefined
      }
      suppressHydrationWarning
    >
      <head>
        <meta charSet="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <script>{prepaintUnderlineEngineScript}</script>
        <link
          rel="icon"
          type="image/png"
          href="/favicon-96x96.png"
          sizes="96x96"
        />
        <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
        <link rel="shortcut icon" href="/favicon.ico" />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/apple-touch-icon.png"
        />
        <meta name="apple-mobile-web-app-title" content="Cartwheel" />
        <link rel="manifest" href="/site.webmanifest" />
        <link rel="describedby" href="/llms.txt" type="text/plain" />
        <meta
          property="og:image"
          content="https://getcartwheel.com/images/cartwheel-share.png"
        />
        <meta name="is-agentic-site-type" content="business" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: siteStructuredData }}
        />
        <script
          async
          src="https://www.googletagmanager.com/gtag/js?id=AW-17677371273"
        />
        <script>{`
          window.dataLayer = window.dataLayer || [];
          function gtag() {
            dataLayer.push(arguments);
          }
          gtag('js', new Date());
          gtag('config', 'AW-17677371273');

          document.addEventListener(
            'click',
            function (event) {
              const button = event.target.closest('button');
              const actionName = button?.getAttribute('data-dd-action-name');
              if (actionName === 'Join now' || actionName === 'Continue with Google') {
                gtag('event', 'conversion', {
                  send_to: 'AW-17677371273/MABXCKaN-rYbEImPne1B',
                });
              }
            },
            true,
          );
        `}</script>
        <Links />
        <Meta />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
        <script>{`
          _linkedin_partner_id = '9211962';
          window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
          window._linkedin_data_partner_ids.push(_linkedin_partner_id);
        `}</script>
        <script>{`
          (function (linkedIn) {
            if (!linkedIn) {
              window.lintrk = function (action, data) {
                window.lintrk.q.push([action, data]);
              };
              window.lintrk.q = [];
            }
            var firstScript = document.getElementsByTagName('script')[0];
            var insightScript = document.createElement('script');
            insightScript.type = 'text/javascript';
            insightScript.async = true;
            insightScript.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
            firstScript.parentNode.insertBefore(insightScript, firstScript);
          })(window.lintrk);
        `}</script>
        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: 'none' }}
            alt=""
            src="https://px.ads.linkedin.com/collect/?pid=9211962&fmt=gif"
          />
        </noscript>
      </body>
    </html>
  );
}

export default function Root() {
  return <Outlet />;
}
